import React, { useEffect, useId, useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import { ReactComponent as CloseIcon } from '@assets/images/Icons/Close.svg'
import { ReactComponent as FolderIcon } from '@assets/images/Icons/Folder.svg'
import Button from '../Button/Button.component'
import Overlay from '../Overlay/Overlay.component'
import Search from '../Search/Search.component'
import type { ModalProps } from './Modal.types'
import './Modal.css'

const Modal: React.FC<ModalProps> = ({
  open = true,
  onClose,
  size = 'sm',
  variant = 'default',
  title,
  description,
  children,
  icon,
  iconTone = 'warning',
  fileIcon,
  fileName,
  fileMeta,
  details,
  folders = [],
  selectedFolderId,
  defaultSelectedFolderId,
  onFolderSelect,
  folderSearchValue,
  defaultFolderSearchValue = '',
  onFolderSearchChange,
  folderSearchPlaceholder = 'Search folder',
  showClose = true,
  closeOnBackdrop = true,
  primaryLabel = 'Primary',
  secondaryLabel = 'Secondary',
  secondaryRightIcon,
  primaryDestructive = false,
  primaryDisabled = false,
  onPrimaryClick,
  onSecondaryClick,
  footerStart,
  showFooter = true,
  inline = false,
  darkMode = false,
  className = '',
  bodyClassName = '',
  style,
  'aria-label': ariaLabel,
}) => {
  const titleId = useId()
  const descriptionId = useId()
  const folderListId = useId()

  const [internalFolderSearch, setInternalFolderSearch] = useState(
    defaultFolderSearchValue,
  )
  const [internalSelectedFolderId, setInternalSelectedFolderId] = useState(
    defaultSelectedFolderId ?? folders[0]?.id ?? '',
  )

  const resolvedFolderSearch = folderSearchValue ?? internalFolderSearch
  const resolvedSelectedFolderId = selectedFolderId ?? internalSelectedFolderId

  const filteredFolders = useMemo(() => {
    const query = resolvedFolderSearch.trim().toLowerCase()
    if (!query) {
      return folders
    }
    return folders.filter((folder) =>
      folder.label.toLowerCase().includes(query),
    )
  }, [folders, resolvedFolderSearch])

  useEffect(() => {
    if (!open || inline || !onClose) {
      return undefined
    }

    const handleKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        onClose()
      }
    }

    window.addEventListener('keydown', handleKeyDown)

    return () => {
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [inline, onClose, open])

  if (!open) {
    return null
  }

  const rootClassNames = [
    'ds-modal',
    `ds-modal--${size}`,
    `ds-modal--${variant}`,
    darkMode ? 'ds-modal--dark' : '',
    inline ? 'ds-modal--inline' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ')

  const handleClose = (): void => {
    onClose?.()
  }

  const handleFolderSearchChange = (nextValue: string): void => {
    if (folderSearchValue === undefined) {
      setInternalFolderSearch(nextValue)
    }
    onFolderSearchChange?.(nextValue)
  }

  const handleFolderSelect = (folderId: string): void => {
    if (selectedFolderId === undefined) {
      setInternalSelectedFolderId(folderId)
    }
    onFolderSelect?.(folderId)
  }

  const closeButton = showClose ? (
    <button
      type="button"
      className="ds-modal__close"
      aria-label="Close modal"
      onClick={handleClose}
    >
      <CloseIcon width={16} height={16} aria-hidden="true" />
    </button>
  ) : null

  const isNotice = variant === 'notice'
  const isFileDetails = variant === 'fileDetails'
  const isMoveFiles = variant === 'moveFiles'
  const usesDefaultHeader =
    variant === 'default' || isFileDetails || isMoveFiles
  const footerButtonSize = isNotice ? 'md' : 'sm'

  const footer =
    showFooter && (primaryLabel || secondaryLabel || footerStart) ? (
      <footer
        className={`ds-modal__footer${
          isNotice ? ' ds-modal__footer--notice' : ''
        }`.trim()}
      >
        {footerStart ? (
          <div className="ds-modal__footer-start">{footerStart}</div>
        ) : null}
        <div className="ds-modal__footer-actions">
          {secondaryLabel ? (
            <Button
              variant="secondary"
              size={footerButtonSize}
              fullWidth={isNotice}
              darkMode={darkMode}
              rightIcon={secondaryRightIcon}
              onClick={onSecondaryClick ?? handleClose}
            >
              {secondaryLabel}
            </Button>
          ) : null}
          {primaryLabel ? (
            <Button
              variant="primary"
              size={footerButtonSize}
              fullWidth={isNotice}
              darkMode={darkMode}
              destructive={primaryDestructive}
              disabled={primaryDisabled}
              onClick={onPrimaryClick}
            >
              {primaryLabel}
            </Button>
          ) : null}
        </div>
      </footer>
    ) : null

  const fileDetailsBody =
    isFileDetails && (fileIcon || fileName || fileMeta || details?.length) ? (
      <>
        {fileIcon || fileName || fileMeta ? (
          <div className="ds-modal__file-summary">
            {fileIcon ? (
              <span className="ds-modal__file-icon" aria-hidden="true">
                {fileIcon}
              </span>
            ) : null}
            <div className="ds-modal__file-copy">
              {fileName ? (
                <p className="ds-modal__file-name">{fileName}</p>
              ) : null}
              {fileMeta ? (
                <p className="ds-modal__file-meta">{fileMeta}</p>
              ) : null}
            </div>
          </div>
        ) : null}
        {details != null && details.length > 0 ? (
          <dl id={descriptionId} className="ds-modal__file-details">
            {details.map((item, index) => (
              <div key={index} className="ds-modal__file-detail-row">
                <dt className="ds-modal__file-detail-label">{item.label}</dt>
                <dd className="ds-modal__file-detail-value">{item.value}</dd>
              </div>
            ))}
          </dl>
        ) : null}
      </>
    ) : null

  const moveFilesBody = isMoveFiles ? (
    <>
      <div className="ds-modal__move-search">
        <Search
          value={resolvedFolderSearch}
          placeholder={folderSearchPlaceholder}
          darkMode={darkMode}
          ariaLabel={folderSearchPlaceholder}
          onChange={handleFolderSearchChange}
        />
      </div>
      <div className="ds-modal__move-folders-wrap">
        <div
          id={folderListId}
          className="ds-modal__move-folders"
          role="listbox"
          aria-label="Folders"
        >
          {filteredFolders.map((folder) => {
            const isSelected = folder.id === resolvedSelectedFolderId
            return (
              <button
                key={folder.id}
                type="button"
                role="option"
                aria-selected={isSelected}
                className={`ds-modal__move-folder${
                  isSelected ? ' ds-modal__move-folder--selected' : ''
                }`.trim()}
                onClick={() => handleFolderSelect(folder.id)}
              >
                <span className="ds-modal__move-folder-icon" aria-hidden="true">
                  {folder.icon ?? <FolderIcon width={24} height={24} />}
                </span>
                <span className="ds-modal__move-folder-label">
                  {folder.label}
                </span>
                {isSelected ? (
                  <span className="ds-modal__move-folder-selected">
                    Selected
                  </span>
                ) : null}
              </button>
            )
          })}
        </div>
      </div>
    </>
  ) : null

  const dialog = (
    <div
      className={rootClassNames}
      style={style}
      role="dialog"
      aria-modal={!inline}
      aria-label={ariaLabel}
      aria-labelledby={titleId}
      aria-describedby={
        description || children || details?.length || isMoveFiles
          ? descriptionId
          : undefined
      }
    >
      {usesDefaultHeader ? (
        <header className="ds-modal__header">
          <h2 id={titleId} className="ds-modal__title">
            {title}
          </h2>
          {closeButton}
        </header>
      ) : null}

      <div
        className={`ds-modal__body ${bodyClassName}`.trim()}
        id={isMoveFiles ? descriptionId : undefined}
      >
        {variant === 'confirmation' ? (
          <div className="ds-modal__confirmation-top">
            <div className="ds-modal__confirmation-heading">
              {icon ? (
                <span
                  className={`ds-modal__icon-wrap ds-modal__icon-wrap--${iconTone}`.trim()}
                  aria-hidden="true"
                >
                  {icon}
                </span>
              ) : null}
              <h2 id={titleId} className="ds-modal__title">
                {title}
              </h2>
            </div>
            {closeButton}
          </div>
        ) : null}

        {variant === 'notice' ? (
          <div className="ds-modal__notice">
            {icon ? (
              <span
                className={`ds-modal__icon-wrap ds-modal__icon-wrap--${iconTone}`.trim()}
                aria-hidden="true"
              >
                {icon}
              </span>
            ) : null}
            <h2 id={titleId} className="ds-modal__title">
              {title}
            </h2>
            {children ? (
              <div id={descriptionId} className="ds-modal__notice-copy">
                {children}
              </div>
            ) : null}
          </div>
        ) : null}

        {fileDetailsBody}
        {moveFilesBody}

        {!isNotice && !isFileDetails && !isMoveFiles && description ? (
          <p id={descriptionId} className="ds-modal__description">
            {Array.isArray(description)
              ? description.filter(Boolean).join(' ')
              : description}
          </p>
        ) : null}

        {!isNotice ? children : null}
      </div>

      {footer}
    </div>
  )

  if (inline) {
    return (
      <>
        <Overlay
          inline
          open
          darkMode={darkMode}
          onClick={closeOnBackdrop ? handleClose : undefined}
        />
        <div className="ds-modal__inline-shell">{dialog}</div>
      </>
    )
  }

  if (typeof document === 'undefined') {
    return dialog
  }

  return createPortal(
    <>
      <Overlay
        open
        darkMode={darkMode}
        onClick={closeOnBackdrop ? handleClose : undefined}
        zIndex={1100}
      />
      <div className="ds-modal__viewport">{dialog}</div>
    </>,
    document.body,
  )
}

export default Modal
